import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlagsInhouse } from 'hooks/use-flags-in-house/use-flags-in-house';
import { selectFlags, selectQueryParamOverrides, setQueryParamOverrides, updateFlags } from 'store/flags.slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { parseJWT } from 'sync/jwt-service/jwt-service';
import { debugLog } from 'utils/debug';
import { Flag, Flags, getDefaultFlags, getStoredFlagOverrides, removeStoredFlagsOverrides, setStoredFlagsOverrides } from 'utils/flags';
import { getFlagsOverridesFromQueryParams } from './use-flag.utils';

export const useFlags = () => {
  const dispatch = useAppDispatch();
  const storedFlags = useAppSelector(selectFlags);
  const queryParamOverrides = useAppSelector(selectQueryParamOverrides);
  const { search } = useLocation();

  const [defaultFlags, setDefaultFlags] = useState({} as Flags);

  // Retrieve flags from in-house solution
  const { triggerFlags } = useFlagsInhouse();

  /**
   * getQueryParamOverrides filters query params to allow them only if the user
   * is not authenticated or if they are authenticated and have a Couchbase
   * email address. This allows Couchbase users to freely distribute links to
   * activate features on-demand without the risk of these links leaking to
   * non-Couchbase users.
   */
  const getQueryParamOverrides = (paramOverrides: Record<string, boolean>) => {
    // useFlags can be used when user is not authenticated, so we need to use
    // the JWT to determine if the user is authenticated but not useUser
    const sessionUser = parseJWT();

    if (!sessionUser || sessionUser.email.endsWith('@couchbase.com')) {
      return paramOverrides;
    }

    return {};
  };

  const updateStore = (updated: { [key: keyof Flags]: boolean }, paramOverrides = queryParamOverrides) => {
    dispatch(
      updateFlags({
        ...updated, // nominal updates such as those from launch darkly
        ...getStoredFlagOverrides(), // overrides win over any updates
        ...getQueryParamOverrides(paramOverrides), // always allow a query param to win
      })
    );
  };

  /**
   * Variation looks up a flag value allowing for overrides directly within the application.
   */
  const variation = (key: keyof Flags) => storedFlags[key];

  /**
   * override saves the provided key/value to local storage and applies the override.
   */
  const override = (key: keyof Flags, value: boolean) => {
    setStoredFlagsOverrides({ ...getStoredFlagOverrides(), [key]: value });
    updateStore({ [key]: value } as Flag);
  };

  /**
   * reset erases the override local storage and updates all flags using the last returned by Launch Darkly and app defaults.
   */
  const reset = () => {
    removeStoredFlagsOverrides();
    updateStore(defaultFlags);
  };

  /**
   * debug returns diagnostic information and is useful for troubleshooting for internal and external users.
   *
   * @param full If true, all feature flags are returned. If false, only those
   * with defaults within the application are returned cutting potentially a
   * hundred or more flags.
   */
  const debug = (full: boolean) => {
    const defaultValues = getDefaultFlags();
    const overrides = getStoredFlagOverrides();

    return Object.entries(storedFlags).reduce<{
      [key: string]: {
        default: boolean;
        override: boolean;
        store: boolean;
      };
    }>((result, [key, value]) => {
      if (!full && !(key in defaultValues)) {
        return result;
      }

      return {
        ...result,
        [key]: {
          default: defaultValues[key],
          override: overrides[key],
          store: !!value,
        },
      };
    }, {});
  };

  const updateOrganizationId = async (organizationId: string | null) => {
    // AV-44828, when our organizational context changes, we need to reflect
    // a change in our feature flags.
    const tenantId = organizationId || '';

    const flagsToUpdate = await triggerFlags(tenantId).then((resp) => {
      return resp.data;
    });

    if (flagsToUpdate) {
      debugLog('/hooks/use-flags/use-flags.ts setupFlagsStore() updating from LD', flagsToUpdate);
      updateStore(flagsToUpdate);
      setDefaultFlags(flagsToUpdate);
    }
  };

  const setInitialQueryParamsOverrides = () => {
    const currentQueryParams = getFlagsOverridesFromQueryParams(new URLSearchParams(search));

    dispatch(setQueryParamOverrides(currentQueryParams));
    updateStore({}, currentQueryParams); // don't wait for launchDarkly to override them
  };

  const setInitialFlags = async () => {
    const { data: flagsToUpdate } = await triggerFlags();
    if (flagsToUpdate) {
      setDefaultFlags(flagsToUpdate);
      updateStore(flagsToUpdate);
    }
  };

  const initialize = async () => {
    setInitialQueryParamsOverrides();
    await setInitialFlags();
  };

  return { updateOrganizationId, initialize, reset, debug, override, variation };
};
